import cn from 'classnames';
import React from 'react';
import { OverridableComponentProps } from 'types';
import EpisodeDate from './EpisodeDate';
import EpisodeDescription from './EpisodeDescription';
import EpisodeDuration from './EpisodeDuration';
import EpisodeTitle from './EpisodeTitle';
import styles from './PodcastInfo.module.scss';
import PodcastName from './PodcastName';

export type PodcastInfoProps<As extends React.ElementType = 'div'> =
  OverridableComponentProps<object, As>;

const VALID_CHILDREN: React.ComponentType[] = [
  PodcastName,
  EpisodeDate,
  EpisodeDescription,
  EpisodeTitle,
  EpisodeDuration,
];

export function PodcastInfo<As extends React.ElementType = 'div'>({
  as,
  children,
  className,
  ...props
}: PodcastInfoProps<As>) {
  const Wrapper = as || 'div';

  // children can be any type of element, but when iterating these classes are
  // applied to EpisodeTitle and PodcastName depending on the order of occurrence
  // in the children list.  The first element has the primary styles applies and the
  // second has the secondary styles applied.
  const classNamePriority = [styles.primary, styles.secondary];

  const getClassName = (child: React.ReactElement) => {
    if (child.type === PodcastName || child.type === EpisodeTitle) {
      return cn(classNamePriority.shift(), child.props.className);
    }

    return child.props.className;
  };

  return (
    <Wrapper className={cn(styles.podcastInfo, className)} {...props}>
      {React.Children.map(children, (child) => {
        if (
          !React.isValidElement(child) ||
          typeof child.type === 'string' ||
          !VALID_CHILDREN.includes(child.type) ||
          child.type === EpisodeDate
        ) {
          return child;
        }

        return React.cloneElement(child, {
          className: getClassName(child),
        });
      })}
    </Wrapper>
  );
}

PodcastInfo.EpisodeDate = EpisodeDate;
PodcastInfo.EpisodeDescription = EpisodeDescription;
PodcastInfo.EpisodeTitle = EpisodeTitle;
PodcastInfo.PodcastName = PodcastName;
PodcastInfo.EpisodeDuration = EpisodeDuration;

export default PodcastInfo;
